import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { branchesAPI } from '../api';
import { useAuth } from './AuthContext';

const BranchContext = createContext();

// Skeleton loader shown while branch data loads
const BranchLoadingSkeleton = () => (
  <div className="min-h-screen bg-primaryB-50 dark:bg-primaryB-900 flex">
    {/* Sidebar skeleton */}
    <div className="hidden md:flex w-64 bg-primaryB-50 dark:bg-primaryB-800 border-r border-primaryB-200 dark:border-primaryB-700 flex-col p-4">
      <div className="h-10 bg-primaryB-200 dark:bg-primaryB-700 rounded-xl mb-6 animate-pulse"></div>
      <div className="space-y-3">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-10 bg-primaryB-200 dark:bg-primaryB-700 rounded-xl animate-pulse"></div>
        ))}
      </div>
    </div>
    {/* Main content skeleton */}
    <div className="flex-1 p-6">
      <div className="h-8 bg-primaryB-200 dark:bg-primaryB-700 rounded w-1/4 mb-6 animate-pulse"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-primaryB-200 dark:bg-primaryB-700 rounded-2xl animate-pulse"></div>
        ))}
      </div>
      <div className="h-64 bg-primaryB-200 dark:bg-primaryB-700 rounded-2xl animate-pulse"></div>
    </div>
  </div>
);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

export const BranchProvider = ({ children }) => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBranches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await branchesAPI.getAll();
      const branchList = response.data.branches || [];
      setBranches(branchList);

      // Set current branch from localStorage or default to main branch
      const savedBranchId = localStorage.getItem('currentBranchId');
      const savedBranch = branchList.find(b => b.id === savedBranchId);

      if (savedBranch && savedBranch.isActive) {
        setCurrentBranch(savedBranch);
      } else {
        // Default to main branch or first active branch
        const mainBranch = branchList.find(b => b.isMain && b.isActive);
        const firstActive = branchList.find(b => b.isActive);
        setCurrentBranch(mainBranch || firstActive || null);
      }
    } catch (err) {
      console.error('Failed to load branches:', err);
      // Don't set error for 401 - let the API interceptor handle auth issues
      // This prevents confusing error messages when auth state is still syncing
      if (err.response?.status !== 401) {
        setError('Failed to load branches');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check role directly to avoid stale closure issues
    const canAccessBranches = user && (
      user.role === 'OWNER' ||
      user.role === 'ADMIN' ||
      user.role === 'MANAGER'
    );

    if (canAccessBranches) {
      loadBranches();
    } else {
      setLoading(false);
    }
  }, [user, loadBranches]);

  const clearError = useCallback(() => setError(null), []);

  const switchBranch = useCallback((branchId) => {
    setBranches(currentBranches => {
      const branch = currentBranches.find(b => b.id === branchId);
      if (branch && branch.isActive) {
        setCurrentBranch(branch);
        localStorage.setItem('currentBranchId', branchId);
      }
      return currentBranches;
    });
  }, []);

  const switchToAllBranches = useCallback(() => {
    setCurrentBranch(null);
    localStorage.removeItem('currentBranchId');
  }, []);

  const refreshBranches = useCallback(() => {
    loadBranches();
  }, [loadBranches]);

  // Memoized derived values
  const activeBranches = useMemo(() => branches.filter(b => b.isActive), [branches]);
  const pendingBranches = useMemo(() => branches.filter(b => !b.isActive), [branches]);
  const isAllBranches = currentBranch === null;

  const value = useMemo(() => ({
    branches,
    currentBranch,
    loading,
    error,
    clearError,
    switchBranch,
    switchToAllBranches,
    refreshBranches,
    getActiveBranches: () => activeBranches,
    getPendingBranches: () => pendingBranches,
    isAllBranches
  }), [branches, currentBranch, loading, error, clearError, switchBranch, switchToAllBranches, refreshBranches, activeBranches, pendingBranches, isAllBranches]);

  // Show skeleton while loading branches
  if (loading) {
    return <BranchLoadingSkeleton />;
  }

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};
